import React, { useState, useEffect, useRef } from 'react';
import { ModelPicker, PromptLink } from './modelInfo';
import { backdropClose } from '../backdrop';

// Facts batch panel: the first pipeline. For every selected point the server
// runs the Researcher pass — one web-search sweep that returns a standalone
// facts document — and drops it into that point's task exchange, exactly as the
// per-point "Gather facts" button does. Points run in parallel lanes.
// What sets this one apart: a point that runs into an exhausted Claude usage
// window is not burned. Its lane parks, waits for the window to reopen and
// re-runs the same point, so an overnight sweep picks itself back up.
// Same chrome as the other batches: scope modal, SSE progress, stop/clear.

// The quest meta ships Researcher messages as summaries (has_body instead of
// the body text); the full loop from …/task-loop still carries body.
const hasFacts = (p) => !!(p && Array.isArray(p.task_loop && p.task_loop.messages)
  && p.task_loop.messages.some((m) => m && m.role === 'tres' && (m.body || m.has_body)));
// The focus (topic) of the point's Researcher variant in view — the one the
// batch re-uses for that point (same rule as the server: active_research_id,
// else the newest facts message). null = no focus.
const activeFocus = (p) => {
  const loop = p && p.task_loop;
  const msgs = ((loop && loop.messages) || []).filter((m) => m && m.role === 'tres' && (m.body || m.has_body));
  if (!msgs.length) return null;
  const id = loop.active_research_id;
  const a = (id && msgs.find((m) => m.id === id)) || msgs[msgs.length - 1];
  return a.focus || null;
};

async function papi(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

function fmtEta(ms) {
  if (ms == null) return '…';
  if (ms <= 0) return '0s';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const STATUS_CHIP = {
  queued: { bg: '#9aa0a6', label: 'queued' },
  running: { bg: '#b06000', label: 'running' },
  waiting: { bg: '#5f6368', label: '⏸ limit wait' },
  done: { bg: '#188038', label: '✓ facts' },
  error: { bg: '#c5221f', label: 'error' },
  stopped: { bg: '#5f6368', label: 'stopped' },
  interrupted: { bg: '#5f6368', label: 'interrupted' },
};

const UNFINISHED = ['queued', 'running', 'waiting', 'interrupted', 'error'];

function PointRow({ bp }) {
  const chip = STATUS_CHIP[bp.status] || STATUS_CHIP.queued;
  const isRunning = bp.status === 'running';
  const waitMin = bp.status === 'waiting' && bp.resume_at
    ? Math.max(0, Math.ceil((bp.resume_at - Date.now()) / 60000))
    : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', borderRadius: 4,
      background: isRunning ? 'rgba(176,96,0,0.10)' : 'transparent', fontSize: 12,
    }}>
      <span style={{
        background: chip.bg, color: '#fff', borderRadius: 10, padding: '1px 8px',
        fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{chip.label}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bp.name}</span>
      {bp.focus && (
        <span style={{ color: '#b06000', fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Focus: ${bp.focus}`}>
          🎯 {bp.focus}
        </span>
      )}
      {waitMin != null && (
        <span style={{ color: '#5f6368', whiteSpace: 'nowrap' }} title="Waiting for the usage window to reopen, then this point runs again">
          ~{waitMin}m
        </span>
      )}
      {bp.status === 'error' && bp.errors && bp.errors.length > 0 && (
        <span title={bp.errors.join('; ')} style={{ color: '#c5221f', whiteSpace: 'nowrap', cursor: 'help' }}>⚠</span>
      )}
    </div>
  );
}

const FactsBatchPanel = ({ questId, runId, locations, pointsByUuid, initialBatch, embedded, onStatus, onChange }) => {
  const [batch, setBatch] = useState(initialBatch || null);
  const [active, setActive] = useState(!!(initialBatch && (initialBatch.status === 'running' || initialBatch.status === 'waiting_limit')));
  const [showScope, setShowScope] = useState(false);
  const [selected, setSelected] = useState({}); // uuid -> bool
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const clearedRef = useRef(false);
  // Held in a ref: the parent passes a fresh arrow every render, and a live
  // dependency would tear the SSE subscription down and up on each of them.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (initialBatch && !batch && !clearedRef.current) {
      setBatch(initialBatch);
      if (initialBatch.status === 'running' || initialBatch.status === 'waiting_limit') setActive(true);
    }
  }, [initialBatch, batch]);

  const points = (locations || []).filter((l) => l.uuid && pointsByUuid && pointsByUuid[l.uuid]);
  // Default scope: every point the Researcher hasn't covered yet.
  const noFactsUuids = points.filter((l) => !hasFacts(pointsByUuid[l.uuid])).map((l) => l.uuid);
  const allUuids = points.map((l) => l.uuid);

  const selectedUuids = points.filter((l) => selected[l.uuid]).map((l) => l.uuid);
  const selectedCount = selectedUuids.length;
  const sameSet = (arr) => arr.length === selectedCount && arr.every((u) => selected[u]);
  const activePreset = sameSet(noFactsUuids) ? 'nofacts' : sameSet(allUuids) ? 'all' : 'custom';
  const setPreset = (uuids) => setSelected(Object.fromEntries(uuids.map((u) => [u, true])));

  useEffect(() => {
    if (!active || !runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'facts_batch') {
        setBatch(msg.batch);
      } else if (msg.type === 'facts_batch_done') {
        setBatch(msg.batch);
        setActive(false);
        // The gathered facts live on the run's points — refetch so the point
        // dialogs show the new Researcher bubbles without a page reload.
        if (onChangeRef.current) onChangeRef.current();
      }
    };
    es.onerror = () => { /* auto-reconnects; ignore */ };
    return () => es.close();
  }, [active, runId]);

  const openScope = () => {
    setError(null);
    setPreset(noFactsUuids);
    setShowScope(true);
  };

  const start = async (body) => {
    setError(null); setBusy(true);
    try {
      const r = await papi(`/pipeline/quest/${questId}/facts-batch`, body);
      clearedRef.current = false;
      setBatch({ status: 'running', total: r.total, done_count: 0, gathered_count: 0, error_count: 0, points: [], eta_ms: null });
      setActive(true);
      setShowScope(false);
      setCollapsed(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const startBatch = () => {
    if (!selectedUuids.length) { setError('Select at least one point'); return; }
    start({ uuids: selectedUuids });
  };

  const stopBatch = async () => {
    setError(null);
    try { await papi(`/pipeline/quest/${questId}/facts-batch/stop`, {}); } catch (e) { setError(e.message); }
  };

  const runUnfinished = () => {
    const uuids = (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).map((bp) => bp.uuid);
    if (!uuids.length) return;
    start({ uuids });
  };

  const clearBatch = async () => {
    if (!window.confirm('Delete this facts batch record? Gathered facts are kept on the points.')) return;
    setError(null); setBusy(true);
    try {
      await papi(`/pipeline/quest/${questId}/facts-batch/clear`, {});
      clearedRef.current = true;
      setBatch(null);
      setActive(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // A batch parked on a usage-limit wait is still live: Stop works, and it
  // resumes by itself — so it counts as running for every control here.
  const running = active && batch && (batch.status === 'running' || batch.status === 'waiting_limit');
  const paused = !!(active && batch && batch.status === 'waiting_limit');
  const done = batch && batch.done_count != null ? batch.done_count : 0;
  const total = batch && batch.total != null ? batch.total : 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const unfinishedCount = batch ? (batch.points || []).filter((bp) => UNFINISHED.includes(bp.status)).length : 0;

  // Report status to the tab shell (badge + running dot) when embedded.
  useEffect(() => {
    if (onStatus) onStatus({ running, done, total, status: batch ? batch.status : null, hasBatch: !!batch });
  }, [running, done, total, batch, onStatus]);

  const effCollapsed = embedded ? false : collapsed;
  const box = embedded
    ? { padding: '4px 2px 2px' }
    : {
        border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px',
        margin: '8px 8px 0', background: 'rgba(176,96,0,0.04)',
      };
  const primaryBtn = { background: '#b06000', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };

  return (
    <div style={box}>
      <div
        onClick={embedded ? undefined : () => setCollapsed((c) => !c)}
        title={embedded ? undefined : (collapsed ? 'Expand' : 'Collapse')}
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: embedded ? 'default' : 'pointer' }}
      >
        {!embedded && <span style={{ fontWeight: 700, color: '#b06000' }}>🔍 Facts</span>}
        {/* The header toggles collapse — the picker must not trigger that. */}
        {!embedded && <span onClick={(e) => e.stopPropagation()} role="presentation"><ModelPicker k="tres" /></span>}
        {!embedded && collapsed && batch && (
          <span style={{ fontSize: 12, color: '#5f6368' }}>
            {batch.done_count || 0}/{batch.total || 0}
            {!running && batch.status ? ` · ${batch.status}` : ''}
          </span>
        )}
        {!running && (
          <button style={primaryBtn} disabled={busy || !points.length} onClick={(e) => { e.stopPropagation(); openScope(); }}>
            Gather facts…{noFactsUuids.length ? ` (${noFactsUuids.length} new)` : ''}
          </button>
        )}
        {running && <button style={stopBtn} onClick={(e) => { e.stopPropagation(); stopBatch(); }}>■ Stop (running points finish)</button>}
        {paused && (
          <span style={{ fontSize: 12, color: '#b06000', background: 'rgba(176,96,0,0.12)', borderRadius: 10, padding: '2px 8px' }}>
            ⏸ usage limit — retry{batch.resume_at ? ` ~${new Date(batch.resume_at).toLocaleTimeString()}` : ''}
          </span>
        )}
        {!running && batch && unfinishedCount > 0 && (
          <button onClick={(e) => { e.stopPropagation(); runUnfinished(); }} disabled={busy} style={{ padding: '6px 12px', borderRadius: 4 }}>
            ↻ Run unfinished ({unfinishedCount})
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {running && !paused && <span style={{ fontSize: 12, color: '#5f6368' }}>ETA {fmtEta(batch.eta_ms)}</span>}
          {!running && batch && (
            <button
              className="remove-location-button"
              onClick={(e) => { e.stopPropagation(); clearBatch(); }}
              disabled={busy}
              title="Delete this facts batch record — gathered facts are kept"
              aria-label="Delete facts batch record"
            >×</button>
          )}
          {!embedded && <span style={{ color: '#b06000', fontSize: 13 }}>{collapsed ? '▸' : '▾'}</span>}
        </span>
      </div>

      {error && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}

      {!effCollapsed && batch && (batch.points || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 6 }}>
            <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: running ? '#b06000' : '#188038', transition: 'width .3s' }} />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>
              {done} / {total}
              {batch.error_count ? ` · ${batch.error_count} failed` : ''}
              {!running && batch.status ? ` · ${batch.status}` : ''}
            </span>
          </div>
          <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 4 }}>
            {(batch.points || []).map((bp) => (
              <PointRow key={bp.uuid} bp={bp} />
            ))}
          </div>
        </div>
      )}

      {showScope && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} {...backdropClose(() => setShowScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 540, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              Gather facts <ModelPicker k="tres" /> <PromptLink k="tres" />
            </h3>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Quick select:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { key: 'nofacts', label: `No facts yet (${noFactsUuids.length})`, uuids: noFactsUuids },
                { key: 'all', label: `All points (${allUuids.length})`, uuids: allUuids },
              ].map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.uuids)}
                  style={{
                    padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12,
                    border: activePreset === p.key ? '2px solid #b06000' : '1px solid var(--border,#ccc)',
                    background: activePreset === p.key ? 'rgba(176,96,0,0.10)' : 'transparent',
                    fontWeight: activePreset === p.key ? 600 : 400,
                  }}
                >{p.label}</button>
              ))}
              <button
                onClick={() => setSelected({})}
                style={{ padding: '4px 10px', borderRadius: 14, cursor: 'pointer', fontSize: 12, border: '1px solid var(--border,#ccc)', background: 'transparent', marginLeft: 'auto' }}
              >Clear</button>
            </div>

            {/* Explicit list of exactly what will run. */}
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
              {points.map((l) => {
                const already = hasFacts(pointsByUuid[l.uuid]);
                const focus = activeFocus(pointsByUuid[l.uuid]);
                return (
                  <label key={l.uuid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 2px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!selected[l.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [l.uuid]: e.target.checked }))} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</span>
                    {focus && (
                      <span style={{ fontSize: 10, color: '#b06000', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Focus of the facts variant in view: ${focus} — this pass re-uses it`}>
                        🎯 {focus}
                      </span>
                    )}
                    {already && (
                      <span style={{ fontSize: 10, color: '#188038', whiteSpace: 'nowrap' }} title="The Researcher has already covered this point — running again appends another facts bubble">
                        ✓ has facts
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <div style={{ fontSize: 12, marginTop: 8 }}>
              <strong>{selectedCount}</strong> point(s) will be researched
              {activePreset !== 'custom' ? '' : ' (custom)'}
            </div>
            <div style={{ fontSize: 12, color: '#b06000', background: 'rgba(176,96,0,0.08)', borderRadius: 6, padding: '6px 8px', marginTop: 6 }}>
              Per point: one Researcher web-search pass, its facts document lands in that point's dialog as a Researcher bubble — the same thing the per-point “Gather facts” button does. Nothing is overwritten: a point that already has facts gets another variant of its Researcher bubble (◀ n / N ▶ in the dialog). A point whose facts variant in view carries a focus (🎯) keeps that focus for this pass; set or change it in the point's dialog. Points run in parallel. If the Claude usage window runs out, the affected points wait for it to reopen and run again by themselves.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowScope(false)}>Cancel</button>
              <button style={primaryBtn} disabled={busy || selectedCount === 0} onClick={startBatch}>Start ({selectedCount})</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FactsBatchPanel;
